import { css } from 'styled-components';

/**
 * Gallery Typography System
 * 
 * Professional typography utilities for the gallery experience.
 * All styles are optional and don't override existing typography.
 */

// Fonts are loaded from index.html so the browser can preconnect and swap them efficiently.
export const galleryFontFaces = css``;

// Typography mixins for consistent gallery text styles
export const galleryTypography = {
  // Display text for hero sections and major headlines
  heroDisplay: css`
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.display};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.hero};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.bold};
    line-height: ${({ theme }) => theme.typography.lineHeight.tight};
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.colors.gallery.charcoal};
  `,

  // Large display text for section headers
  sectionDisplay: css`
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.display};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.display};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.semibold};
    line-height: ${({ theme }) => theme.typography.lineHeight.snug};
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.colors.gallery.charcoal};
  `,

  // Gallery section headings
  sectionHeading: css`
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.section};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.semibold};
    line-height: ${({ theme }) => theme.typography.lineHeight.snug};
    letter-spacing: 0;
    color: ${({ theme }) => theme.colors.gallery.charcoal};
  `,

  // Artwork titles
  artworkTitle: css`
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.title};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.medium};
    line-height: ${({ theme }) => theme.typography.lineHeight.snug};
    color: ${({ theme }) => theme.colors.gallery.charcoal};
  `,

  // Artist names and metadata
  artworkMetadata: css`
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.metadata};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.normal};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    color: ${({ theme }) => theme.colors.gallery.neutrals.dark};
  `,

  // Artwork captions and detailed information
  artworkCaption: css`
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.caption};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.caption};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.normal};
    line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
    color: ${({ theme }) => theme.colors.gallery.neutrals.medium};
  `,

  // Gallery body text
  galleryBody: css`
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.normal};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    color: ${({ theme }) => theme.colors.gallery.charcoal};
  `,

  // Elegant body text for descriptions
  elegantBody: css`
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.light};
    line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
    color: ${({ theme }) => theme.colors.gallery.neutrals.dark};
  `,

  // Small text for credits and fine print
  smallText: css`
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.normal};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    color: ${({ theme }) => theme.colors.gallery.neutrals.medium};
  `,
};

// Responsive typography helpers
export const responsiveTypography = {
  // Hero text that scales with screen size
  responsiveHero: css`
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.display};
    
    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      font-size: ${({ theme }) => theme.typography.fontSize.gallery.hero};
    }
    
    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
      font-size: ${({ theme }) => theme.typography.fontSize.gallery.massive};
    }
  `,

  // Section headings that scale appropriately
  responsiveSection: css`
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.title};
    
    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      font-size: ${({ theme }) => theme.typography.fontSize.gallery.section};
    }
    
    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
      font-size: ${({ theme }) => theme.typography.fontSize.gallery.display};
    }
  `,

  // Body text with comfortable reading sizes
  responsiveBody: css`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    
    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
      font-size: ${({ theme }) => theme.typography.fontSize.lg};
    }
  `,
};

// Typography animation utilities
export const typographyAnimations = {
  // Elegant fade-in for headings
  fadeInHeading: css`
    opacity: 0;
    transform: translateY(20px);
    animation: fadeInUp 0.8s ease-out forwards;
    
    @keyframes fadeInUp {
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,

  // Subtle fade-in for body text
  fadeInBody: css`
    opacity: 0;
    animation: fadeIn 0.6s ease-out 0.2s forwards;
    
    @keyframes fadeIn {
      to {
        opacity: 1;
      }
    }
  `,

  // Staggered animation for multiple text elements
  staggeredFadeIn: (delay: number = 0) => css`
    opacity: 0;
    transform: translateY(10px);
    animation: staggerFadeIn 0.6s ease-out ${delay}s forwards;
    
    @keyframes staggerFadeIn {
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `,
};

// Text selection styling for gallery content
export const galleryTextSelection = css`
  ::selection {
    background-color: ${({ theme }) => theme.colors.gallery.accent.primary};
    color: ${({ theme }) => theme.colors.gallery.card};
  }

  ::-moz-selection {
    background-color: ${({ theme }) => theme.colors.gallery.accent.primary};
    color: ${({ theme }) => theme.colors.gallery.card};
  }
`;

// Focus styles for gallery text elements
export const galleryFocusStyles = css`
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.gallery.accent.primary};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.gallery.subtle};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;
